import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Nav } from '../components/Nav';
import { Footer } from '../components/Footer';
import { Globe, Search, BarChart3, Key, Code, RefreshCw, Link as LinkIcon, Eye, FlaskConical, MousePointerClick, Zap, ShieldCheck, Clock, Unlink, Image as ImageIcon, Smartphone, AlertTriangle, UserX, Database, HelpCircle, CheckCircle2, XCircle, ArrowRight, Plug, Gift, Sparkles, Server, LayoutTemplate, Activity, ArrowUpRight } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const painPoints = [{
  icon: <Clock size={28} data-id="element-2385" data-mp="38mu2zv5p84jg" />,
  title: 'The Developer Bottleneck',
  desc: 'You need a simple text change or a new landing page, but you have to wait two weeks for your web guy to get around to it.'
}, {
  icon: <HelpCircle size={28} data-id="element-2386" data-mp="2mcme990kyp6d" />,
  title: 'The SEO Black Box',
  desc: "You're paying an agency for 'SEO' every month, but you have no idea what they actually do or if it's even working."
}, {
  icon: <AlertTriangle size={28} data-id="element-2387" data-mp="2clrnpsgax3f4" />,
  title: 'The Plugin Nightmare',
  desc: "You click 'update' on a simple plugin and suddenly half your website is broken. Now you're scrambling to fix it."
}, {
  icon: <UserX size={28} data-id="element-2388" data-mp="3594bg5y77ha0" />,
  title: 'The Stale Content Trap',
  desc: 'Your last blog post was from 2022. Your services have changed, but your website still tells the old story.'
}];
const platforms = [{
  name: 'WordPress',
  domain: 'wordpress.org'
}, {
  name: 'Shopify',
  domain: 'shopify.com'
}, {
  name: 'Webflow',
  domain: 'webflow.com'
}, {
  name: 'Wix',
  domain: 'wix.com'
}, {
  name: 'Squarespace',
  domain: 'squarespace.com'
}, {
  name: 'GoHighLevel',
  domain: 'gohighlevel.com'
}, {
  name: 'Ghost',
  domain: 'ghost.org'
}, {
  name: 'Magento',
  domain: 'magento.com'
}];
const hostingBenefits = ['Enterprise-grade cloud hosting', 'Daily automated backups', 'Free SSL certificates', 'Global CDN for lightning speed', 'Built-in visual page builder', 'Unlimited bandwidth & storage'];
const features = [{
  icon: <Globe size={22} data-id="element-2389" data-mp="ylnabfslkowt" />,
  title: 'Auto Page Creation',
  desc: 'MR spots a new trend or keyword and automatically builds a fully optimized landing page to capture that traffic.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Search size={22} data-id="element-2390" data-mp="oo8uniei045k" />,
  title: 'On-Page SEO',
  desc: 'Titles, descriptions, headers, and tags are constantly tweaked and optimized to keep you ranking high.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <FlaskConical size={22} data-id="element-2391" data-mp="fnayfmaax0xj" />,
  title: 'A/B Split Testing',
  desc: 'MR tests different headlines, buttons, and layouts against each other to find out what actually gets people to buy.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Eye size={22} data-id="element-2392" data-mp="3s6dddmkgsby4" />,
  title: 'Competitor Watch',
  desc: 'If a competitor changes their pricing or launches a new feature, MR sees it and alerts you instantly.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Zap size={22} data-id="element-2393" data-mp="515wgp4jbtdt" />,
  title: 'Speed Optimization',
  desc: 'MR constantly compresses images, minifies code, and tweaks settings so your site loads in a blink.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <ShieldCheck size={22} data-id="element-2394" data-mp="eysgfq0oyb4n" />,
  title: 'Security & Patching',
  desc: 'Hackers never sleep, and neither does MR. Vulnerabilities are patched before they become problems.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <RefreshCw size={22} data-id="element-2395" data-mp="oqg9twgt628r" />,
  title: 'Content Refreshing',
  desc: 'MR finds old, outdated blog posts and updates them with fresh stats and info to bring them back to life.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <LinkIcon size={22} data-id="element-2396" data-mp="1hsfpr3yrprmu" />,
  title: 'Smart Internal Linking',
  desc: 'As new pages are built, MR automatically links them to your existing content to boost your SEO authority.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Code size={22} data-id="element-2397" data-mp="1qlst6zugkbn6" />,
  title: 'Schema Markup',
  desc: 'MR adds the hidden code that helps Google understand your business, getting you those rich search results.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Activity size={22} data-id="element-2398" data-mp="yv66pnxcug64" />,
  title: 'Uptime Monitoring',
  desc: 'MR checks your site every 60 seconds. If it goes down, MR starts fixing it before you even notice.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <ImageIcon size={22} data-id="element-2399" data-mp="17wmi9yzwoobx" />,
  title: 'Image Management',
  desc: 'Every image is automatically resized, compressed, and given the right alt-text for visually impaired users and SEO.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Smartphone size={22} data-id="element-2400" data-mp="2o7qgrbnp375z" />,
  title: 'Mobile Perfect',
  desc: 'MR ensures every new page looks flawless on phones, tablets, and desktops without you having to check.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Key size={22} data-id="element-2401" data-mp="7mlffg1pyf38" />,
  title: 'Keyword Tracking',
  desc: 'Watch your rankings climb. MR tracks exactly where you stand for the searches that matter to your business.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Unlink size={22} data-id="element-2402" data-mp="3tzlb7fhkf74r" />,
  title: 'Broken Link Fixing',
  desc: 'Nothing hurts SEO like a 404 error. MR finds broken links and redirects them automatically.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <MousePointerClick size={22} data-id="element-2403" data-mp="3g1wriaz8i61a" />,
  title: 'Conversion Tracking',
  desc: 'Know exactly where your leads come from. MR tracks every click, form fill, and phone call.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <BarChart3 size={22} data-id="element-2404" data-mp="1eb1pv2icjcmn" />,
  title: 'Plain English Reports',
  desc: 'No confusing charts. Just a simple weekly summary of how much traffic you got and what MR did to improve it.',
  color: 'bg-purple/10 text-purple'
}];
const withoutMr = ['Waiting weeks for a developer to make simple text changes', 'Paying thousands for SEO without knowing what they do', "Website goes down and you don't know until a customer complains", 'Plugins break your site when you try to update them', 'Old, outdated content making your business look abandoned', 'No idea why people are leaving your site without buying'];
const withMr = ['Changes happen instantly just by asking', 'SEO is handled automatically, 24/7, with clear results', 'Site is monitored every 60 seconds and auto-healed', 'Updates are tested safely before going live', 'Content is constantly refreshed and expanded', 'A/B tests run automatically to get you more customers'];
export function SelfBuildingWebsite() {
  return <div className="w-full bg-white" data-id="element-2405" data-mp="2218odvfn1yue">
      <Nav data-id="element-2406" data-mp="224ehdlre1kve" />

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative bg-[#0F1D3A] pt-44 pb-24 px-6 overflow-hidden" data-id="element-2407" data-mp="264okmqnspx6k">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple/15 rounded-full blur-[120px] pointer-events-none" data-id="element-2408" data-mp="1yjkzo9pwriq3" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-teal/10 rounded-full blur-[100px] pointer-events-none" data-id="element-2409" data-mp="1tkrkmcuz3jnt" />

        <div className="max-w-5xl mx-auto relative z-10 text-center" data-id="element-2410" data-mp="28g7jqwaeu5r2">
          <motion.div initial="hidden" animate="visible" variants={stagger} data-id="element-2411" data-mp="23k0fd7xnx2b8">
            <motion.span variants={fadeUp} className="inline-block text-teal text-sm font-bold tracking-widest uppercase mb-4" data-id="element-2412" data-mp="1fuxw5vndr1em">
              Self-Building Website
            </motion.span>
            <motion.h1 variants={fadeUp} className="font-poppins font-extrabold text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6" data-id="element-2413" data-mp="1pl0r41e2apfl">
              Your Website. <br className="hidden md:block" data-id="element-2414" data-mp="x1ah2jlazi18" />
              <span className="text-gradient" data-id="element-2415" data-mp="s53lnkqj9oxj">Finally Building Itself.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed" data-id="element-2416" data-mp="6lsc46udq5go">
              Stop paying developers for simple changes. Stop wondering if your
              SEO is working. MR builds pages, writes content, runs split tests,
              and keeps everything secure — 24/7.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center mb-12" data-id="element-2417" data-mp="pkmqhepxl3wb">
              <Link to="/contact" className="btn-primary shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow" data-id="element-2418" data-mp="uguzsbhg62vk">
                Talk to MR
              </Link>
              <Link to="/how-it-works" className="btn-ghost hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] transition-shadow" data-id="element-2419" data-mp="18bdxwaqcyk82">
                See How It Works
              </Link>
            </motion.div>

            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-6" data-id="element-2420" data-mp="2xp3sypqavw3j">
              {[{
              value: '24/7',
              label: 'Monitoring & Security'
            }, {
              value: '100+',
              label: 'SEO Factors Checked'
            }, {
              value: 'Zero',
              label: 'Developer Fees'
            }].map(stat => <div key={stat.label} className="bg-white/5 border border-white/10 rounded-xl px-6 py-3 text-center" data-id="element-2421" data-mp="37oun2qay8933">
                  <div className="text-2xl font-bold text-white" data-id="element-2422" data-mp="40kml0r4ia45">
                    {stat.value}
                  </div>
                  <div className="text-xs text-gray-400 font-medium" data-id="element-2423" data-mp="3vep1hcax25pg">
                    {stat.label}
                  </div>
                </div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ PAIN SECTION ═══════════════ */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-2424" data-mp="wxy29usxpwky">
        <div className="max-w-6xl mx-auto" data-id="element-2425" data-mp="wyr88tk2ki21">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2426" data-mp="svbf4777kzu3">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2427" data-mp="11uoqj5ew7yhg">
              Sound Familiar?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2428" data-mp="1pm3o6kvq5rmd">
              If managing your website feels like a constant headache of broken
              plugins and expensive developers, you're not alone.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid md:grid-cols-2 gap-6" data-id="element-2429" data-mp="2985h5qbcsa4b">
            {painPoints.map(p => <motion.div key={p.title} variants={fadeUp} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2430" data-mp="1lh9qwxmjpmz6">
                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center text-red-500 mb-4" data-id="element-2431" data-mp="1blybse786dqw">
                  {p.icon}
                </div>
                <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-2" data-id="element-2432" data-mp="12ki2k9s5cho9">
                  {p.title}
                </h3>
                <p className="text-gray-500 leading-relaxed" data-id="element-2433" data-mp="17hqpeno8p9vu">{p.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ VISUAL GRAPHIC: YOUR WEBSITE HAS A BRAIN ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2434" data-mp="2zh4kk4fssqk8">
        <div className="max-w-5xl mx-auto" data-id="element-2435" data-mp="2zfth65q9lktn">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={fadeUp} className="relative bg-gradient-to-br from-[#0F1D3A] to-[#1A447A] rounded-2xl p-10 md:p-14 overflow-hidden" data-id="element-2436" data-mp="2vgudeog9ru3s">
            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-teal/20 rounded-full blur-[100px] pointer-events-none" data-id="element-2437" data-mp="3j428kdjzhms3" />
            <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-purple/20 rounded-full blur-[100px] pointer-events-none" data-id="element-2438" data-mp="27qqbcliqc2ew" />

            <div className="relative z-10 flex flex-col md:flex-row items-center gap-12" data-id="element-2439" data-mp="22qlu2t9zz3nm">
              <div className="flex-1" data-id="element-2440" data-mp="iae05ihtz2pl">
                <div className="inline-flex items-center gap-2 bg-teal/20 text-teal px-4 py-1.5 rounded-full text-sm font-semibold mb-6" data-id="element-2441" data-mp="ibfo2njy3joq">
                  <Activity size={16} data-id="element-2442" data-mp="raj7xtqxs34a" /> Live Optimization Engine
                </div>
                <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-6" data-id="element-2443" data-mp="w0zklbmxnkpl">
                  Your Website Finally Has a Brain.
                </h2>
                <p className="text-gray-300 text-lg leading-relaxed mb-6" data-id="element-2444" data-mp="10zkk18ud20cz">
                  Most websites just sit there waiting for you to do something.
                  MR turns your site into a living, breathing engine that
                  constantly improves itself.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed" data-id="element-2445" data-mp="1ezbvb1tht2na">
                  It watches how visitors behave, tests new layouts, fixes
                  broken links, and creates new content based on what people are
                  actually searching for —{' '}
                  <strong className="text-white" data-id="element-2446" data-mp="2lga9i64nivbj">all in real-time.</strong>
                </p>
              </div>

              {/* Custom Graphic UI */}
              <div className="w-full md:w-[45%] relative" data-id="element-2447" data-mp="2hht1zlhbjitl">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple/10 to-transparent animate-pulse rounded-2xl" data-id="element-2448" data-mp="2vdwvtdoa5p5j" />

                <div className="flex flex-col gap-4 relative z-10" data-id="element-2449" data-mp="2qebrenqccolf">
                  {/* Metric Card 1 */}
                  <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10 flex items-center gap-4 transform translate-x-4" data-id="element-2450" data-mp="1xrh99xgzy1b4">
                    <div className="w-10 h-10 rounded-lg bg-teal/20 flex items-center justify-center text-teal" data-id="element-2451" data-mp="2chyc9l9a4za4">
                      <ArrowUpRight size={20} data-id="element-2452" data-mp="27ocaio4nmft3" />
                    </div>
                    <div data-id="element-2453" data-mp="1jyrplzoxx8h6">
                      <div className="text-xs text-gray-400 font-medium" data-id="element-2454" data-mp="1jy83e6s9bxxc">
                        Conversion Rate
                      </div>
                      <div className="text-lg font-bold text-white flex items-center gap-2" data-id="element-2455" data-mp="3qntdvuej0hi7">
                        4.2%{' '}
                        <span className="text-xs text-teal bg-teal/10 px-2 py-0.5 rounded-full" data-id="element-2456" data-mp="3vftk24l6w6n8">
                          +1.2%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Central MR Action */}
                  <div className="bg-gradient-to-r from-purple/20 to-teal/20 backdrop-blur-md rounded-xl p-5 border border-white/20 shadow-[0_0_30px_rgba(124,58,237,0.2)] z-20" data-id="element-2457" data-mp="d96tmeupmte6">
                    <div className="flex items-center gap-3 mb-3" data-id="element-2458" data-mp="490k1424udka">
                      <Sparkles size={18} className="text-purple" data-id="element-2459" data-mp="4auufofs2na4" />
                      <span className="text-sm font-bold text-white" data-id="element-2460" data-mp="96s7nlvqvdio">
                        MR Action Executed
                      </span>
                    </div>
                    <div className="text-sm text-gray-300" data-id="element-2461" data-mp="2txsuyc0u8v8t">
                      "A/B test concluded. Variant B increased signups by 18%.
                      Variant B is now live for all users."
                    </div>
                  </div>

                  {/* Metric Card 2 */}
                  <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10 flex items-center gap-4 transform -translate-x-4" data-id="element-2462" data-mp="ps11yiidz0fy">
                    <div className="w-10 h-10 rounded-lg bg-purple/20 flex items-center justify-center text-purple" data-id="element-2463" data-mp="lpxq3srt1tu5">
                      <Search size={20} data-id="element-2464" data-mp="gp9o6c21fsl5" />
                    </div>
                    <div data-id="element-2465" data-mp="br89027qgdey">
                      <div className="text-xs text-gray-400 font-medium" data-id="element-2466" data-mp="bqyf43jkhm3z">
                        New Keyword Found
                      </div>
                      <div className="text-sm font-bold text-white" data-id="element-2467" data-mp="2s38iqcm2a05">
                        "AI Business Automation"
                      </div>
                      <div className="text-xs text-teal mt-1" data-id="element-2468" data-mp="216o5ah0k64nf">
                        ✓ Landing page generated
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ HOW IT ACTUALLY WORKS (TIMELINE) ═══════════════ */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-2469" data-mp="3ou3lraebpi6a">
        <div className="max-w-5xl mx-auto" data-id="element-2470" data-mp="3ovepxbbokrxx">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-16" data-id="element-2471" data-mp="3stkq20i0k99v">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2472" data-mp="355ba3f7hefqn">
              How It Actually Works
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2473" data-mp="kfc8dnh152wh">
              Let's look at a real-world scenario. Here's what happens when a
              competitor tries to outrank you.
            </motion.p>
          </motion.div>

          <div className="relative" data-id="element-2474" data-mp="3s8fubec72xm4">
            {/* Connecting Line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple via-teal to-purple opacity-20 hidden md:block" data-id="element-2475" data-mp="9tezoa3b1ngw" />

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-80px'
          }} variants={stagger} className="space-y-12" data-id="element-2476" data-mp="51duf84k0x0d">
              {/* Step 1 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2477" data-mp="srq6mv6u1fwr">
                <div className="md:w-1/2 flex justify-end" data-id="element-2478" data-mp="2uj4g2rjnphbp">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 w-full md:w-[400px] relative" data-id="element-2479" data-mp="2ulpfzb0e9n6t">
                    <div className="absolute top-6 -right-3 w-6 h-6 bg-white border-t border-r border-gray-100 rotate-45 hidden md:block" data-id="element-2480" data-mp="2gdubx3otojeh" />
                    <div className="text-sm font-bold text-gray-500 mb-2" data-id="element-2481" data-mp="2kffwzcbig3zr">
                      Step 1: The Threat
                    </div>
                    <p className="text-gray-800 text-sm font-medium" data-id="element-2482" data-mp="2bnno3624x2xa">
                      A competitor publishes a massive new guide on "Local
                      Marketing" and starts stealing your search traffic.
                    </p>
                  </div>
                </div>
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center font-bold text-sm z-10 shadow-sm hidden md:flex" data-id="element-2483" data-mp="21n3kvxv21ocf">
                  1
                </div>
                <div className="md:w-1/2" data-id="element-2484" data-mp="3i4rd64nlzuct" />
              </motion.div>

              {/* Step 2 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2485" data-mp="3n1rigdcmwt2v">
                <div className="md:w-1/2" data-id="element-2486" data-mp="z8b6bc9o0fq7" />
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-purple text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2487" data-mp="z932opb36hhj">
                  2
                </div>
                <div className="md:w-1/2 flex justify-start" data-id="element-2488" data-mp="pg5cry3rlq8w">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 w-full md:w-[400px] relative" data-id="element-2489" data-mp="1d8lxkgevzwbw">
                    <div className="absolute top-6 -left-3 w-6 h-6 bg-white border-b border-l border-gray-100 rotate-45 hidden md:block" data-id="element-2490" data-mp="1i52vu3fsvr2p" />
                    <div className="text-sm font-bold text-purple mb-2 flex items-center gap-2" data-id="element-2491" data-mp="188gc5hz7n629">
                      <Eye size={14} data-id="element-2492" data-mp="3ox3qzby6abrx" /> Step 2: MR Detects
                    </div>
                    <p className="text-gray-600 text-sm" data-id="element-2493" data-mp="3sz8hi1tbqsac">
                      MR's competitor monitoring spots the new page, analyzes
                      its keywords, and identifies exactly why it's ranking.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Step 3 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2494" data-mp="3vcu5up99opyi">
                <div className="md:w-1/2 flex justify-end" data-id="element-2495" data-mp="32p7qykul9ywc">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 w-full md:w-[400px] relative" data-id="element-2496" data-mp="32pps9vw3z3b4">
                    <div className="absolute top-6 -right-3 w-6 h-6 bg-white border-t border-r border-gray-100 rotate-45 hidden md:block" data-id="element-2497" data-mp="2xoa8s89kx4uu" />
                    <div className="text-sm font-bold text-teal mb-2 flex items-center gap-2" data-id="element-2498" data-mp="37o142c5yrnvz">
                      <Code size={14} data-id="element-2499" data-mp="2jy6vw7wlani2" /> Step 3: MR Responds
                    </div>
                    <p className="text-gray-600 text-sm" data-id="element-2500" data-mp="2ezvhdjvcnoti">
                      MR automatically drafts a better, more comprehensive page
                      for your site, complete with optimized images and schema
                      markup.
                    </p>
                  </div>
                </div>
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-teal text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2501" data-mp="3dft4mkb4o086">
                  3
                </div>
                <div className="md:w-1/2" data-id="element-2502" data-mp="2lqpyt03lehtf" />
              </motion.div>

              {/* Step 4 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2503" data-mp="2pqs2tfwsvnbv">
                <div className="md:w-1/2" data-id="element-2504" data-mp="2zoy3ju6venf5" />
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-purple text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2505" data-mp="2zmld0sn1r4qh">
                  4
                </div>
                <div className="md:w-1/2 flex justify-start" data-id="element-2506" data-mp="1zh129mj4113k">
                  <div className="bg-purple/5 p-6 rounded-xl shadow-sm border border-purple/20 w-full md:w-[400px] relative" data-id="element-2507" data-mp="1ziknzsnjc4cq">
                    <div className="absolute top-6 -left-3 w-6 h-6 bg-[#f9f5ff] border-b border-l border-purple/20 rotate-45 hidden md:block" data-id="element-2508" data-mp="1pqew0ixa9mxj" />
                    <div className="text-sm font-bold text-purple mb-2 flex items-center gap-2" data-id="element-2509" data-mp="1kr28dvyl8btw">
                      <Sparkles size={14} data-id="element-2510" data-mp="1grl250bc7ano" /> Step 4: You Win
                    </div>
                    <p className="text-gray-700 text-sm italic" data-id="element-2511" data-mp="1bpvn79kzucub">
                      "Hey! I noticed a competitor trying to outrank us for
                      Local Marketing. I built a better page, published it, and
                      we've already reclaimed the #1 spot."
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════ WORKS WITH ANY PLATFORM ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2512" data-mp="347zm5hgd14pb">
        <div className="max-w-5xl mx-auto" data-id="element-2513" data-mp="3969i3u9ws9bz">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2514" data-mp="39588lu8jjuwd">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-teal/10 text-teal px-4 py-1.5 rounded-full text-sm font-semibold mb-4" data-id="element-2515" data-mp="2lgf4vblogir7">
              <Plug size={16} data-id="element-2516" data-mp="2bjaro19t7xmj" /> Works With Any Platform
            </motion.div>
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2517" data-mp="2ghvlniygxl2j">
              We Integrate With What You Already Use.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2518" data-mp="26imt8uj94eia">
              Already have a website you love? Keep it. MR plugs right into your
              existing CMS and makes it smarter, faster, and fully automated.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid grid-cols-2 sm:grid-cols-4 gap-6" data-id="element-2519" data-mp="1wtzdzgy5w6oy">
            {platforms.map(tool => <motion.div key={tool.name} variants={fadeUp} className="bg-gray-50 rounded-xl p-5 flex flex-col items-center gap-3 border border-gray-200 hover:border-purple/40 hover:shadow-md transition-all duration-300" data-id="element-2520" data-mp="2fmbnqor5aa94">
                <img src={`https://logo.clearbit.com/${tool.domain}`} alt={tool.name} className="w-10 h-10 rounded-lg object-contain" onError={e => {
              const target = e.target as HTMLImageElement;
              target.src = `https://www.google.com/s2/favicons?domain=${tool.domain}&sz=128`;
            }} data-id="element-2521" data-mp="2klobdbpublcr" />
                <span className="text-sm font-semibold text-gray-700" data-id="element-2522" data-mp="1w06ju8l54on7">
                  {tool.name}
                </span>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ 1DASH INCLUDED ═══════════════ */}
      <section className="py-20 px-6 bg-[#0F1D3A]" data-id="element-2523" data-mp="29zeo3fw5umy9">
        <div className="max-w-5xl mx-auto" data-id="element-2524" data-mp="2a0pp57y81glo">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} data-id="element-2525" data-mp="2eyivc0ziyaly">
            <motion.div variants={fadeUp} className="text-center mb-12" data-id="element-2526" data-mp="kjopce13sw37">
              <span className="inline-flex items-center gap-2 bg-purple/10 text-purple px-4 py-1.5 rounded-full text-sm font-semibold mb-4" data-id="element-2527" data-mp="d0yz24tzhrtr">
                <Gift size={16} data-id="element-2528" data-mp="897f4d5c2kcl" /> Included With Your Complete Package
              </span>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4" data-id="element-2529" data-mp="3c7be8vyhton">
                Need a New Site? Get 1Dash —{' '}
                <span className="text-gradient" data-id="element-2530" data-mp="3ufdv05yhq27d">On Us.</span>
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-2" data-id="element-2531" data-mp="vzb8zsspz5qe">
                If your current site is beyond repair, we've got you covered.
                When you go with our complete package, you get{' '}
                <strong className="text-white" data-id="element-2532" data-mp="m3qcga6f6xo1">
                  1Dash Website Builder & Hosting
                </strong>{' '}
                included at no extra cost.
              </p>
              <p className="text-gray-500 text-sm" data-id="element-2533" data-mp="36t7f6xitri6l">
                Already love your site? That's totally fine — this is an option,
                not a requirement.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-10" data-id="element-2534" data-mp="15rreb89c0tev">
              <div className="flex flex-col md:flex-row items-center gap-6 mb-8" data-id="element-2535" data-mp="wq09s6qj2sml">
                <div className="flex items-center gap-3" data-id="element-2536" data-mp="11njm313o0vbw">
                  <div className="w-12 h-12 rounded-xl bg-purple/20 flex items-center justify-center" data-id="element-2537" data-mp="11q5uf2ydrev6">
                    <LayoutTemplate size={24} className="text-purple" data-id="element-2538" data-mp="1pg04xdv88rc9" />
                  </div>
                  <div data-id="element-2539" data-mp="1thc0e00s86sd">
                    <h3 className="text-xl font-poppins font-bold text-white" data-id="element-2540" data-mp="1fo2ptw13lf89">
                      1Dash Website Builder
                    </h3>
                    <span className="text-xs text-gray-400 bg-white/5 px-2 py-0.5 rounded-full" data-id="element-2541" data-mp="1klco816gt188">
                      Enterprise Hosting Included
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2542" data-mp="2mi0kbtvjfspw">
                {hostingBenefits.map(b => <div key={b} className="flex items-start gap-3" data-id="element-2543" data-mp="2igykpbxplene">
                    <CheckCircle2 size={18} className="text-teal shrink-0 mt-0.5" data-id="element-2544" data-mp="2iem2qyryb4ac" />
                    <span className="text-gray-300 text-sm" data-id="element-2545" data-mp="2r9s0dah2ig1s">{b}</span>
                  </div>)}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ FEATURES GRID ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2546" data-mp="2dhc2sp6xra89">
        <div className="max-w-6xl mx-auto" data-id="element-2547" data-mp="2di577jif9nn8">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2548" data-mp="28i90vdm95k4i">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2549" data-mp="3jx635p7g65tt">
              Everything MR Handles For You
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2550" data-mp="17vyj4bwmc34c">
              All the technical details, SEO tweaks, and content updates that
              usually take hours of your day — MR takes care of it.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-2551" data-mp="1rh33zfpegx8y">
            {features.map(f => <motion.div key={f.title} variants={fadeUp} className="bg-gray-50 rounded-xl p-5 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all group" data-id="element-2552" data-mp="2f7z02p0w2em0">
                <div className={`w-10 h-10 rounded-xl ${f.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`} data-id="element-2553" data-mp="2htyaaaw7ena1">
                  {f.icon}
                </div>
                <h3 className="font-poppins font-semibold text-gray-900 mb-2" data-id="element-2554" data-mp="2vrmxvqhno8is">
                  {f.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed" data-id="element-2555" data-mp="30py8w4josp2l">
                  {f.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ BEFORE & AFTER ═══════════════ */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-2556" data-mp="3ffnce6f95561">
        <div className="max-w-5xl mx-auto" data-id="element-2557" data-mp="3fhgjfmts1rek">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2558" data-mp="3jhh7lz6ohgyt">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2559" data-mp="1obg7b9f68ffn">
              The Difference Is Night and Day
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid md:grid-cols-2 gap-8" data-id="element-2560" data-mp="1tbugiusidvf9">
            {/* Without MR */}
            <motion.div variants={fadeUp} className="bg-red-50 border border-red-200 rounded-2xl p-8" data-id="element-2561" data-mp="2h16s21bb6fkq">
              <h3 className="font-poppins font-bold text-xl text-red-700 mb-6 flex items-center gap-2" data-id="element-2562" data-mp="mogw7e1dqwap">
                <XCircle size={24} data-id="element-2563" data-mp="hpdrxvt84u3s" /> Without MR
              </h3>
              <ul className="space-y-4" data-id="element-2564" data-mp="hr6vh28jiy7k">
                {withoutMr.map(item => <li key={item} className="flex items-start gap-3 text-red-700/80" data-id="element-2565" data-mp="2td95eg7glikt">
                    <XCircle size={16} className="shrink-0 mt-1 text-red-400" data-id="element-2566" data-mp="2kkdzdka63itz" />
                    <span className="text-sm leading-relaxed" data-id="element-2567" data-mp="2kikzqoxz1yvh">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            {/* With MR */}
            <motion.div variants={fadeUp} className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8" data-id="element-2568" data-mp="2akws4yf9owvw">
              <h3 className="font-poppins font-bold text-xl text-emerald-700 mb-6 flex items-center gap-2" data-id="element-2569" data-mp="21mbcpc53dxzc">
                <CheckCircle2 size={24} data-id="element-2570" data-mp="25o6cgc7dk3b0" /> With MR
              </h3>
              <ul className="space-y-4" data-id="element-2571" data-mp="3vzu5k8o6gseb">
                {withMr.map(item => <li key={item} className="flex items-start gap-3 text-emerald-700/80" data-id="element-2572" data-mp="3vxq560phmbis">
                    <CheckCircle2 size={16} className="shrink-0 mt-1 text-emerald-500" data-id="element-2573" data-mp="3b9guf4nveqlm" />
                    <span className="text-sm leading-relaxed" data-id="element-2574" data-mp="369kkypwem6yo">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ FINAL CTA ═══════════════ */}
      <section className="py-24 px-6 bg-[#0F1D3A] text-center" data-id="element-2575" data-mp="2sd751l7iqdmb">
        <div className="max-w-3xl mx-auto" data-id="element-2576" data-mp="2nkntetse5sk5">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} data-id="element-2577" data-mp="2njmicpbdl62f">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-white mb-6" data-id="element-2578" data-mp="28nnbfi005byv">
              Ready for a Website That Actually Works For You?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-400 text-lg mb-10 max-w-xl mx-auto" data-id="element-2579" data-mp="k2zecdplwhpb">
              Stop paying for simple updates and guessing if your SEO is
              working. Let MR handle it all.
            </motion.p>
            <motion.div variants={fadeUp} data-id="element-2580" data-mp="y4bryihao781">
              <Link to="/contact" className="btn-primary shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow text-lg px-10 py-4" data-id="element-2581" data-mp="y30pqn3hz7j1">
                Talk to MR
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-2582" data-mp="f5g03dsws4xd" />
    </div>;
}